import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown } from 'lucide-react';
import type { AppModule } from '@overseer/contracts';
import { Logo } from '@/components/ui/logo';
import { MODULES } from '@/lib/modules';
import { useChoosePreference } from '@/lib/preferences';
import { cn } from '@/lib/cn';

/**
 * The Overseer mark, and the way between applications.
 *
 * ## With one module it is not a control
 *
 * No chevron, no menu, no hover state, not focusable. **A user with one
 * application must see exactly what they saw before the split** — an affordance
 * suggesting there is somewhere else to go, when there is not, is worse than no
 * affordance at all. That is every real user today, which is why it is the case
 * this component gets right first.
 *
 * ## Switching goes to the module root
 *
 * Not to "the equivalent screen", because there is no equivalent screen — HR's
 * timesheet has no counterpart in the hotel. Guessing at one is how a switcher
 * lands somebody on a 404.
 *
 * Switching also **sets the default**, so the choice made by using the app is
 * the choice remembered. Nobody should have to find a setting to say "start me
 * here"; the last module used is the honest answer.
 */
export const ModuleSwitcher = ({
  modules,
  current,
}: {
  modules: AppModule[];
  current: AppModule | null;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const remember = useChoosePreference();
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);

  // Close on an outside click or on Escape. Both, because a menu that traps the
  // user until they click the trigger again is a menu people click twice.
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const mark = (
    <>
      <Logo className="size-6 shrink-0 text-brand" />
      <div className="hidden min-w-0 text-left sm:block">
        <p className="truncate text-sm leading-tight font-semibold tracking-tight">
          {t('app.name')}
        </p>
        <p className="truncate text-xs leading-tight text-fg-muted">
          {/* The module's name once there is more than one, so the header says
              which application you are in. Otherwise the product tagline. */}
          {current && modules.length > 1 ? t(MODULES[current].labelKey) : t('app.tagline')}
        </p>
      </div>
    </>
  );

  if (modules.length <= 1) {
    return <div className="flex min-w-0 shrink-0 items-center gap-2">{mark}</div>;
  }

  const choose = (module: AppModule) => {
    setOpen(false);
    remember.defaultModule(module);
    void navigate({ to: MODULES[module].to as never });
  };

  return (
    <div ref={container} className="relative flex min-w-0 shrink-0 items-center">
      <button
        type="button"
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={t('modules.switch')}
        className="flex min-w-0 items-center gap-2 rounded-md px-1 py-1 hover:bg-bg-hover focus-visible:outline-2 focus-visible:outline-ring"
      >
        {mark}
        <ChevronDown aria-hidden className="size-4 shrink-0 text-fg-muted" />
      </button>

      {open ? (
        <div
          role="menu"
          aria-label={t('modules.switch')}
          className="absolute top-full left-0 z-50 mt-1 w-64 rounded-md border border-border-default bg-bg-raised p-1 shadow-lg"
        >
          {modules.map((module) => {
            const definition = MODULES[module];
            const active = module === current;

            return (
              <button
                key={module}
                type="button"
                role="menuitem"
                onClick={() => choose(module)}
                className={cn(
                  'flex w-full items-start gap-2 rounded-md px-2 py-2 text-left hover:bg-bg-hover',
                  active ? 'bg-bg-hover' : null,
                )}
              >
                <definition.icon aria-hidden className="mt-0.5 size-4 shrink-0 text-fg-muted" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">
                    {t(definition.labelKey)}
                  </span>
                  <span className="block truncate text-xs text-fg-muted">
                    {t(definition.descriptionKey)}
                  </span>
                </span>
                {active ? (
                  <Check aria-hidden className="mt-0.5 size-4 shrink-0 text-brand" />
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};
